/**
 * Client-side identifiers for backend activity tracking. Shared by every portal; ported from
 * public-ui-lib's `activity-session.util.ts`.
 *
 * Three levels, sent as headers on every API request and stored by the backend on each event:
 *   X-Client-Session-Id  this tab's visit
 *   X-Flow-Id            one explicit multi-request flow (e.g. a service-agreement order)
 *   X-Request-Id         this request only
 *
 * Keys must be id-safe and at most 128 characters; one that fails validation is dropped
 * server-side.
 */

export const ACTIVITY_HEADERS = {
  REQUEST_ID: 'X-Request-Id',
  CLIENT_SESSION_ID: 'X-Client-Session-Id',
  FLOW_ID: 'X-Flow-Id',
} as const;

/** The Angular portals' key, so the dealer portal's id survives a reload the same way. */
export const CLIENT_SESSION_STORAGE_KEY = 'tt.activity.client-session-id';

/** Generate an activity id. */
export function newActivityId(): string {
  // randomUUID needs a secure context; dev hosts served over plain http fall back.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 14)}`;
}

export interface ClientSession {
  /** This tab's client session id, created on first use. */
  id(): string;
  /** Start a new session — on logout, so the next user does not extend this one's chronology. */
  reset(): void;
}

/**
 * `persist: true` keeps the id in `sessionStorage` (one tab's visit; a reload continues it) —
 * the dealer portal, whose users are logged in.
 *
 * `persist: false` keeps it in memory only — the customer portal. Writing an identifier to the
 * device is storage under cookiebekendtgørelsen §3 whatever the API, customers never consent to
 * anything, and grouping their requests is not strictly necessary; a reload starts a new session.
 */
export function createClientSession({ persist }: { persist: boolean }): ClientSession {
  let inMemory: string | null = null;

  return {
    id() {
      if (!persist) {
        inMemory ??= newActivityId();
        return inMemory;
      }
      try {
        const stored = sessionStorage.getItem(CLIENT_SESSION_STORAGE_KEY);
        if (stored) {
          return stored;
        }
        const created = newActivityId();
        sessionStorage.setItem(CLIENT_SESSION_STORAGE_KEY, created);
        return created;
      } catch {
        inMemory ??= newActivityId();
        return inMemory;
      }
    },
    reset() {
      inMemory = null;
      if (persist) {
        try {
          sessionStorage.removeItem(CLIENT_SESSION_STORAGE_KEY);
        } catch {
          // Storage unavailable — the in-memory id is already cleared.
        }
      }
    },
  };
}

let activeFlowId: string | null = null;

/**
 * The flow the user is inside, so every request made during it carries the same `X-Flow-Id`.
 * One at a time: these flows own the screen, and a second `start` means a restart, not a branch.
 */
export const activityFlow = {
  /** Begin a flow (replacing any running one) and return its id. */
  start(): string {
    activeFlowId = newActivityId();
    return activeFlowId;
  },
  /** End the active flow. Safe to call when none is running. */
  end(): void {
    activeFlowId = null;
  },
  /** The active flow's id, or null. */
  current(): string | null {
    return activeFlowId;
  },
};

/** The activity headers for one request. */
export function activityHeaders(session: ClientSession): Record<string, string> {
  const flowId = activityFlow.current();
  return {
    [ACTIVITY_HEADERS.REQUEST_ID]: newActivityId(),
    [ACTIVITY_HEADERS.CLIENT_SESSION_ID]: session.id(),
    ...(flowId ? { [ACTIVITY_HEADERS.FLOW_ID]: flowId } : {}),
  };
}
